import { useState, type ClipboardEvent, type DragEvent, type ReactNode } from 'react'
import { Send } from 'lucide-react'
import { AttachmentDropzone } from './AttachmentDropzone'
import type { AttachmentDraft } from '../../hooks/useAttachmentUpload'
import { filesFromClipboard } from '@/utils/clipboard'

/**
 * Caixa de texto recriada a cada envio (`key` novo), já com o foco. Esvaziar
 * o mesmo elemento com o foco nele fazia o Chrome repintar só o trecho onde
 * estava o texto enviado — o placeholder voltava cortado ("Escre").
 */
export function useFreshTextarea() {
  const [generation, setGeneration] = useState(0)
  return {
    key: generation,
    autoFocus: generation > 0,
    renew: () => setGeneration((g) => g + 1),
  }
}

/** Ctrl+V de print na caixa de texto vira anexo. */
export function pasteFilesHandler(addFiles: (files: File[]) => void) {
  return (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFromClipboard(e.clipboardData)
    if (files.length === 0) return
    e.preventDefault()
    addFiles(files)
  }
}

interface ComposerFrameProps {
  /** A caixa de texto (simples ou com @menção). */
  children: ReactNode
  /** Faixa acima da caixa (ex.: "Respondendo a …"). */
  banner?: ReactNode
  /** Botões depois do clipe (ex.: "@"). */
  tools?: ReactNode
  drafts: AttachmentDraft[]
  onAddFiles: (files: File[]) => void
  onRemoveDraft: (localId: string) => void
  /** Tipos aceitos no clipe (ex.: só imagens no HUB). */
  accept?: string
  /** Há texto digitado — com anexo pronto também dá para enviar. */
  hasText: boolean
  isSending: boolean
  isUploading: boolean
  hasErrors: boolean
  onSend: () => void
  /** Editor fechado (ex.: ocorrência finalizada): mostra só o motivo. */
  disabledReason?: string | null
  /** Painéis estreitos: menos respiro em volta. */
  compact?: boolean
}

/**
 * Moldura dos editores de conversa (Ocorrências e Atendimento HUB): caixa
 * grande, barra de baixo com clipe (+ ferramentas) e "Enviar". Arrastar
 * arquivo para a área anexa. Quem usa cuida só do texto e do envio.
 */
export function ComposerFrame({
  children,
  banner,
  tools,
  drafts,
  onAddFiles,
  onRemoveDraft,
  accept,
  hasText,
  isSending,
  isUploading,
  hasErrors,
  onSend,
  disabledReason,
  compact = false,
}: ComposerFrameProps) {
  const pad = compact ? 'px-4 pb-4 pt-2' : 'px-8 pb-7 pt-2'
  if (disabledReason) {
    return (
      <div className={pad}>
        <div className="rounded-xl border border-border-subtle bg-surface-muted px-4 py-4 text-center text-sm text-muted">
          {disabledReason}
        </div>
      </div>
    )
  }

  // O drop padrão do navegador abriria o arquivo numa aba nova.
  const handleDragOver = (e: DragEvent) => {
    if (Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault()
  }
  const handleDrop = (e: DragEvent) => {
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length === 0) return
    e.preventDefault()
    onAddFiles(files)
  }

  const canSubmit =
    !isSending &&
    !isUploading &&
    !hasErrors &&
    (hasText || drafts.some((d) => d.status === 'ready'))

  return (
    // Drop-zone: arrastar não tem equivalente de teclado (o clipe tem).
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={pad} onDragOver={handleDragOver} onDrop={handleDrop}>
      {/* Sem `overflow-hidden`: o popup de @menção abre para fora da caixa
          (acima dela) e seria cortado. Os cantos ficam com a faixa de
          cima e a barra de baixo. */}
      <div className="rounded-xl border border-border-subtle bg-surface transition focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 dark:focus-within:ring-blue-900/40">
        {banner}
        {children}
        <div className="flex items-center justify-between gap-3 rounded-b-xl border-t border-border-subtle bg-surface-muted/60 px-3 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <AttachmentDropzone
              drafts={drafts}
              onAddFiles={onAddFiles}
              onRemove={onRemoveDraft}
              accept={accept}
              disabled={isSending}
            />
            {tools}
          </div>
          <button
            type="button"
            onClick={onSend}
            disabled={!canSubmit}
            className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
            title={
              isUploading
                ? 'Aguardando upload de anexos...'
                : hasErrors
                  ? 'Remova os anexos com erro antes de enviar.'
                  : undefined
            }
          >
            <Send className="h-4 w-4" />
            {isSending ? 'Enviando…' : isUploading ? 'Aguarde…' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Classe da caixa de texto dentro da moldura. */
export const COMPOSER_TEXTAREA_CLASS =
  'block w-full resize-y border-0 bg-transparent px-4 py-4 text-[15px] text-body placeholder:text-muted focus:outline-none focus:ring-0'
